import { Validators } from '@angular/forms'
import { Observable } from 'rxjs/Observable'

import {
  PromptFactory,
  BasePrompt,
  RadioPrompt,
  LabelPrompt,
  TextboxPrompt,
  ControlTypes,
  Form,
  CheckboxPrompt,
  MultiRelationshipPrompt,
  RichTextareaPrompt,
  DropdownPrompt
} from '../../models'
import { Question, AnswerChoice, QUESTION_TYPES_ID_MAPPING, UserAnswer, AnswerChoiceGroup, QuestionType, DISPLAY_TYPE_ID_MAP } from '../../../data/models'

export class QuestionViewFormMapper {
  private _formChoiceQuestionTypestrategyRegistry: Map<
  string,
  (answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]) => Array<BasePrompt<AnswerChoice | string>>
  >
  private _formTextQuestionTypestrategyRegistry: Map<
  string,
  (userAnswers: UserAnswer[]) => Array<BasePrompt<string>>
  >
  private _formChoiceDiplayTypetrategyRegistry: Map<
  string,
  (answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]) => Array<BasePrompt<AnswerChoice | string>>
  >

  public constructor(private _promptFactory: PromptFactory) {
    this._formChoiceQuestionTypestrategyRegistry = new Map<
      string,
      (answerChoiceGroup: AnswerChoiceGroup) => Array<BasePrompt<AnswerChoice | string>>
      >()
    this._formTextQuestionTypestrategyRegistry = new Map<
      string,
      (userAnswers: UserAnswer[]) => Array<BasePrompt<string>>
      >()
    this._formChoiceDiplayTypetrategyRegistry = new Map<
    string,
    (answerChoiceGroup: AnswerChoiceGroup) => Array<BasePrompt<AnswerChoice | string>>
    >()

    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.MULTIPLE_CHOICE, this._radioStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.QUANTITATIVE_COMPARISON, this._radioStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.NUMERIC_ENTRY, this._numericEntryStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.MULTI_SELECT, this._multiSelectStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.TEXT_COMPLETION, this._radioStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.MULTI_SOURCE_REASONING, this._radioStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.FLASH_CARD, this._flashCardStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.SENTENCE_SELECT, this._singleChoiceLabelStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.TWO_PART_ANALYSIS,this._radioStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.TABLE_ANALYSIS, this._radioStrategy)
    this._formChoiceQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.GRAPHICS_INTERPRETATION, this._dropdownStrategy)
    this._formTextQuestionTypestrategyRegistry.set(QUESTION_TYPES_ID_MAPPING.ESSAY, this._essayStrategy)

    this._formChoiceDiplayTypetrategyRegistry.set(DISPLAY_TYPE_ID_MAP.MULTIPLE_CHOICE, this._radioStrategy)
  }

  public toForm(question: Question, userAnswers = []): Form<Question> {
    const form = new Form(question)

    return question.answerChoices.length > 0 ?
      this._toFormChoiceStrategy(form, userAnswers) :
      this._toFormTextStrategy(form, userAnswers)
  }

  private _toFormTextStrategy(form: Form<Question>, userAnswers: UserAnswer[]): Form<Question> {
    const promptStrategy = this._getQuestionTypeStrategy(this._formTextQuestionTypestrategyRegistry, form.model.questionType)
    form.prompts = promptStrategy.call(this, userAnswers)
    return form
  }

  private _toFormChoiceStrategy(form: Form<Question>, userAnswers: UserAnswer[]): Form<Question> {
    const promptStrategy = this._getQuestionTypeStrategy(this._formChoiceQuestionTypestrategyRegistry, form.model.questionType)
    const prompt = new MultiRelationshipPrompt()
    prompt.key = 'answerChoiceGroup'
    form.prompts.push(prompt)

    let promptForm
    form.model.answerChoiceGroups.forEach((answerChoiceGroup) => {
      promptForm = new Form(answerChoiceGroup)
      promptForm.prompts = promptStrategy.call(this, answerChoiceGroup, userAnswers)
      prompt.forms.push(promptForm)
    })
    return form
  }

  private _getQuestionTypeStrategy(store, questionType: QuestionType) {
    const strategy = store.get(questionType.id) || this._getDisplayTypeStrategy(questionType)
    if (!strategy) {
      throw new Error(`No question view form strategy registered for question type id ${questionType.id}`)
    }
    return strategy
  }

  private _getDisplayTypeStrategy(questionType: QuestionType) {
    return questionType.displayType ? this._formChoiceDiplayTypetrategyRegistry.get(questionType.displayType.id) : null
  }

  private _radioStrategy(answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]): RadioPrompt[] {
    const options = this._defaultPromptOptions(answerChoiceGroup.answerChoices)
    options['value'] = this._answerChoiceValue(answerChoiceGroup.answerChoices, userAnswers)[0]

    return [this._promptFactory.get(ControlTypes.Radio, options) as RadioPrompt]
  }

  private _dropdownStrategy(answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]): DropdownPrompt[] {
    const options = this._defaultPromptOptions(answerChoiceGroup.answerChoices)
    options['value'] = this._answerChoiceValue(answerChoiceGroup.answerChoices, userAnswers)[0]
    options['label'] = answerChoiceGroup.getLabel("text").value
    return [this._promptFactory.get(ControlTypes.Dropdown, options) as DropdownPrompt]
  }

  private _flashCardStrategy(answerChoiceGroup: AnswerChoiceGroup, _userAnswers: UserAnswer[]): LabelPrompt[] {
    const options = this._defaultPromptOptions(answerChoiceGroup.answerChoices)
    const answerChoice = answerChoiceGroup.answerChoices[0]
    options['value'] = answerChoice.value

    return [this._promptFactory.get(ControlTypes.Label, options) as LabelPrompt]
  }

  private _multiSelectStrategy(answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]): CheckboxPrompt[] {
    const options = this._defaultPromptOptions(answerChoiceGroup.answerChoices)
    options['value'] = this._answerChoiceValue(answerChoiceGroup.answerChoices, userAnswers)
    return [this._promptFactory.get(ControlTypes.Checkbox, options) as CheckboxPrompt]
  }

  private _essayStrategy(userAnswers: UserAnswer[]): RichTextareaPrompt[] {
    const userAnswer = userAnswers[0] || { value: '' }
    const options = {
      key: 'essay_text',
      value: userAnswer.value,
      height: '455px',
      menubar: false,
      toolbar: 'cut | paste | undo | redo'
    }

    return [this._promptFactory.get(ControlTypes.RichTextarea, options) as RichTextareaPrompt]
  }

  private _singleChoiceLabelStrategy(answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]): LabelPrompt[] {
    const userAnswer = userAnswers[0] || { value: '' }
    const options = this._defaultPromptOptions(answerChoiceGroup.answerChoices)
    options['value'] = userAnswer.value
    return [this._promptFactory.get(ControlTypes.Label, options) as LabelPrompt]
  }

  private _defaultPromptOptions(answerChoices: AnswerChoice[]) {
    const formOptions = Observable.of(answerChoices.map((answerChoice) => {
      return { value: answerChoice.id, label: answerChoice.value }
    }))
    return {
      key: 'answerChoice',
      options: formOptions,
      validators: [Validators.required]
    }
  }

  private _numericEntryStrategy(answerChoiceGroup: AnswerChoiceGroup, userAnswers: UserAnswer[]): TextboxPrompt[] {
    const nullUserAnswer = { value: '' }
    const topUserAnswer = userAnswers.find((userAnswer) => userAnswer.answerChoice.id === answerChoiceGroup.answerChoices[0].id)
      || nullUserAnswer
    const bottomUserAnswer = userAnswers.find((userAnswer) => userAnswer.answerChoice.id === answerChoiceGroup.answerChoices[1].id)
      || nullUserAnswer
    const topPrompt = this._createNumericPrompt('topPrompt', topUserAnswer.value)
    const bottomPrompt = this._createNumericPrompt('bottomPrompt', bottomUserAnswer.value)
    return [topPrompt, bottomPrompt]
  }

  private _createNumericPrompt(key, value): TextboxPrompt {
    const options = {
      key: key,
      type: 'text',
      value: value,
      validators: [Validators.required, Validators.pattern(/^\d+$/)]
    }
    return this._promptFactory.get(ControlTypes.Textbox, options) as TextboxPrompt
  }

  private _answerChoiceValue(answerChoices: AnswerChoice[], userAnswers: UserAnswer[]): string[] {
    const answerChoiceIds = answerChoices.map((answerChoice) => answerChoice.id)
    return userAnswers.reduce((previous, userAnswer) => {
      if (answerChoiceIds.includes(userAnswer.answerChoice.id)) {
        previous.push(userAnswer.answerChoice.id)
      }
      return previous
    }, [])
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/forms/services/question-view-form-mapper/question-view-form.mapper.ts